/** Типы записей и их описание — без зависимостей, используется и сервером, и приложением. */

export const EVENT_TYPES = [
  'breast',
  'bottle',
  'solid',
  'sleep',
  'diaper',
  'walk',
  'medicine',
  'temperature',
  'measure',
  'note',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

/** Категория определяет цвет в интерфейсе и попадание в сводки. */
export type EventCategory = 'feed' | 'sleep' | 'diaper' | 'walk' | 'other';

export const EVENT_META: Record<EventType, { label: string; category: EventCategory; timed: boolean }> = {
  breast: { label: 'Грудь', category: 'feed', timed: true },
  bottle: { label: 'Бутылочка', category: 'feed', timed: true },
  solid: { label: 'Прикорм', category: 'feed', timed: false },
  sleep: { label: 'Сон', category: 'sleep', timed: true },
  diaper: { label: 'Подгузник', category: 'diaper', timed: false },
  walk: { label: 'Прогулка', category: 'walk', timed: true },
  medicine: { label: 'Лекарство', category: 'other', timed: false },
  temperature: { label: 'Температура', category: 'other', timed: false },
  measure: { label: 'Замер', category: 'other', timed: false },
  note: { label: 'Заметка', category: 'other', timed: false },
};

/** Типы, у которых есть длительность и может идти таймер. */
export const TIMED_TYPES: readonly EventType[] = EVENT_TYPES.filter((t) => EVENT_META[t].timed);

export const FEED_TYPES: readonly EventType[] = ['breast', 'bottle', 'solid'];

export function isEventType(v: unknown): v is EventType {
  return typeof v === 'string' && (EVENT_TYPES as readonly string[]).includes(v);
}

/** Запись в том виде, в каком её отдаёт API. */
export interface BabyEvent {
  id: string;
  childId: string;
  type: EventType;
  startedAt: string;
  endedAt: string | null;
  payload: Record<string, unknown>;
  note: string | null;
  authorId: number;
  authorName: string | null;
  editedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

const num = (v: unknown) => String(v).replace('.', ',');

interface Seg {
  side: 'left' | 'right';
  from: string;
  to: string | null;
}

/**
 * Чистое время записи в минутах (без пауз). Для груди с отрезками — сумма отрезков,
 * иначе — от начала до конца (или до now, если идёт).
 */
export function activeMinutes(e: Pick<BabyEvent, 'type' | 'startedAt' | 'endedAt' | 'payload'>, now = Date.now()): number {
  const segs = e.type === 'breast' ? (e.payload.segments as Seg[] | undefined) : undefined;
  if (segs?.length) {
    const end = e.endedAt ? Date.parse(e.endedAt) : now;
    return segs.reduce((s, g) => s + Math.max(0, (g.to ? Date.parse(g.to) : end) - Date.parse(g.from)), 0) / 60000;
  }
  const end = e.endedAt ? Date.parse(e.endedAt) : now;
  return Math.max(0, end - Date.parse(e.startedAt)) / 60000;
}

/** Минуты по сторонам груди. */
export function breastSides(e: Pick<BabyEvent, 'startedAt' | 'endedAt' | 'payload'>, now = Date.now()): { left: number; right: number; last: 'left' | 'right' | null } {
  const segs = e.payload.segments as Seg[] | undefined;
  const end = e.endedAt ? Date.parse(e.endedAt) : now;
  if (!segs?.length) {
    const m = Math.max(0, end - Date.parse(e.startedAt)) / 60000;
    const side = e.payload.side;
    if (side === 'left') return { left: m, right: 0, last: 'left' };
    if (side === 'right') return { left: 0, right: m, last: 'right' };
    return { left: m / 2, right: m / 2, last: null };
  }
  const out = { left: 0, right: 0, last: segs[segs.length - 1]!.side };
  for (const g of segs) out[g.side] += Math.max(0, (g.to ? Date.parse(g.to) : end) - Date.parse(g.from)) / 60000;
  return out;
}

/** Закрыть кормление грудью к моменту end: закрыть открытый отрезок, снять паузу, вычислить сторону. */
export function finalizeBreast(payload: Record<string, unknown>, endIso: string): Record<string, unknown> {
  const segs = (payload.segments as Seg[] | undefined)?.map((g) => ({ ...g, to: g.to ?? endIso }));
  const out: Record<string, unknown> = { ...payload, pausedAt: null, alarmAt: null };
  if (segs?.length) {
    out.segments = segs;
    const sides = new Set(segs.map((g) => g.side));
    out.side = sides.size > 1 ? 'both' : segs[0]!.side;
  }
  return out;
}

/** Короткая подпись записи для ленты и бота: «Бутылочка 120 мл», «Грудь · левая». */
export function describeEvent(e: Pick<BabyEvent, 'type' | 'payload'> & { startedAt?: string }): string {
  const p = e.payload;
  switch (e.type) {
    case 'breast': {
      const side = p.side === 'left' ? 'левая' : p.side === 'right' ? 'правая' : 'обе';
      return `Грудь · ${side}`;
    }
    case 'bottle':
      return `Бутылочка ${String(p.ml)} мл${typeof p.preparedMl === 'number' && p.preparedMl !== p.ml ? ` из ${p.preparedMl}` : ''}${p.kind === 'breast_milk' ? ' · сцеженное' : ''}`;
    case 'solid':
      return `Прикорм · ${String(p.food)}${p.amount ? `, ${String(p.amount)}` : ''}`;
    case 'diaper': {
      const map: Record<string, string> = { wet: 'мокрый', dirty: 'грязный', mixed: 'мокрый и грязный', dry: 'сухой' };
      return `Подгузник · ${map[String(p.kind)] ?? ''}`;
    }
    case 'medicine':
      return `Лекарство · ${String(p.name)}${p.dose ? `, ${String(p.dose)}` : ''}`;
    case 'temperature':
      return `Температура ${Number(p.celsius).toFixed(1).replace('.', ',')}°`;
    case 'measure': {
      const parts: string[] = [];
      if (typeof p.weightG === 'number') parts.push(`${(p.weightG / 1000).toFixed(2).replace('.', ',')} кг`);
      if (typeof p.heightCm === 'number') parts.push(`${num(p.heightCm)} см`);
      if (typeof p.headCm === 'number') parts.push(`голова ${num(p.headCm)} см`);
      return `Замер · ${parts.join(', ')}`;
    }
    case 'note':
      return String(p.text);
    case 'sleep': {
      if (p.settling) return `Укладываем${Array.isArray(p.methods) && p.methods.length ? ` · ${(p.methods as string[]).join(', ')}` : ''}`;
      const settle = typeof p.settleStartedAt === 'string' && e.startedAt ? Math.round((Date.parse(e.startedAt) - Date.parse(p.settleStartedAt)) / 60000) : null;
      const settleText = settle != null && settle > 0 ? `укладывали ${settle} мин${Array.isArray(p.methods) && p.methods.length ? ` (${(p.methods as string[]).join(', ').toLowerCase()})` : ''}` : null;
      const parts = [settleText, p.place, p.position, Array.isArray(p.clothes) && p.clothes.length ? (p.clothes as string[]).join(', ') : null, typeof p.roomTemp === 'number' ? `${num(p.roomTemp)}°` : null].filter(Boolean);
      return parts.length ? `Сон · ${parts.join(' · ')}` : 'Сон';
    }
    default:
      return EVENT_META[e.type].label;
  }
}
